import { SkyButton, SkyDropdown, SkyText } from "@skypointcloud/skypoint-ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  NOTIFY_OUTCOMES,
  NOTIFY_OUTCOME_LABELS,
  NOTIFY_SUBJECT_LABELS,
  type NotifyOutcome,
  type NotifySubject,
  type TestStatusView,
} from "@shared/types/notifications";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { useMonitorEnabled } from "@/hooks/use-monitor";
import { shownSubjects } from "@/lib/notifications/rulesDraft";
import {
  TEST_STATUS_GIVE_UP_MS,
  TEST_STATUS_POLL_MS,
  TEST_STATUS_SLOW_POLL_MS,
  notificationDeliveriesKey,
  useInstanceTestStatus,
  useSendInstanceTest,
} from "@/hooks/use-notifications";
import { ApiClientError, failureMessage } from "@/lib/api";
import { reportFailureShown } from "@/lib/feedback/failure";
import {
  INSTANCE_TEST_NO_TARGETS,
  INSTANCE_TEST_WAITING_TOO_LONG,
  instanceTestRateLimitedSentence,
  instanceTestResultSentence,
  retryAtOf,
  testRetryingSentence,
} from "@/lib/notifications/sentences";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * "Send a test notification" (contract-notif R10, §4.5; round-1 ruling 9, Leftover 4).
 *
 * The test goes to one SAVED cell's own targets (its addresses and, with its Teams switch on,
 * the instance channel), never to L1 and never to a typed address; the pickers say which cell.
 * The web only writes the outbox row (202 with its id), and this line reads the result from the
 * status route every 2 s. #2158: a delivery can end after the 90 s give-up line (a refused key is
 * tried once more after 90 s), so the status is still read every 10 s after that line while the
 * tab is open, and a try that already failed is said with its cause while it is tried again.
 *
 * Every result stays in the line that said "Sending…" (ux-s10 §3 rule 3): a `role=status` line
 * beside the button that persists until the next test. Focus stays on the button.
 */
type Phase =
  | { kind: "idle" }
  | { kind: "waiting"; eventId: string; tooLong: boolean }
  | { kind: "done"; text: string };

function subjectOption(subject: NotifySubject) {
  return { value: subject, text: NOTIFY_SUBJECT_LABELS[subject] };
}

function outcomeOption(outcome: NotifyOutcome) {
  return { value: outcome, text: NOTIFY_OUTCOME_LABELS[outcome] };
}

/** What the line says when the POST itself was refused (§4.5). */
function refusalSentence(error: unknown, zone: string): string {
  if (error instanceof ApiClientError) {
    if (error.status === 429 || error.code === "RATE_LIMITED") {
      return instanceTestRateLimitedSentence(retryAtOf(error.details), zone);
    }
    if (error.status === 409 && error.code === "CONFLICT") return INSTANCE_TEST_NO_TARGETS;
  }
  return `The test could not be sent. ${failureMessage(error, "Try again.")}`;
}

/**
 * The line beside the button: the result, a failed try while it is tried again, "Sending…" while
 * out, the give-up line, or nothing.
 */
function testLine(phase: Phase, sending: boolean, answer: TestStatusView | undefined): string {
  if (phase.kind === "done") return phase.text;
  if (phase.kind === "waiting") {
    const retrying = answer ? testRetryingSentence(answer) : null;
    if (retrying) return retrying;
    return phase.tooLong ? INSTANCE_TEST_WAITING_TOO_LONG : "Sending…";
  }
  return sending ? "Sending…" : "";
}

function pollIntervalOf(phase: Phase): number | false {
  if (phase.kind !== "waiting") return false;
  return phase.tooLong ? TEST_STATUS_SLOW_POLL_MS : TEST_STATUS_POLL_MS;
}

export function TestNotificationButton() {
  const zone = useDisplayTz();
  // Data health only while skyMonitor is on, as the rules above.
  const subjects = shownSubjects(useMonitorEnabled());
  const queryClient = useQueryClient();
  const { selectedInstance } = useTenantInstance();
  const send = useSendInstanceTest();
  const [subject, setSubject] = useState<NotifySubject>("import");
  const [outcome, setOutcome] = useState<NotifyOutcome>("failure");
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });

  const eventId = phase.kind === "waiting" ? phase.eventId : null;
  const status = useInstanceTestStatus(eventId, pollIntervalOf(phase));

  // After 90 s the line says so and the reads slow to every 10 s; the result still lands here.
  useEffect(() => {
    if (phase.kind !== "waiting" || phase.tooLong) return;
    const handle = setTimeout(
      () =>
        setPhase((current) =>
          current.kind === "waiting" ? { ...current, tooLong: true } : current,
        ),
      TEST_STATUS_GIVE_UP_MS,
    );
    return () => clearTimeout(handle);
  }, [phase]);

  // A finished test is a new row in Recent deliveries: read it once, not on a timer.
  const instanceId = selectedInstance?.id;
  useEffect(() => {
    if (phase.kind !== "done") return;
    void queryClient.invalidateQueries({ queryKey: notificationDeliveriesKey(instanceId) });
  }, [phase, queryClient, instanceId]);

  // The status read is folded into the line during render, once per answer.
  const answer = status.data;
  if (phase.kind === "waiting" && answer && answer.eventId === phase.eventId) {
    if (answer.status !== "waiting") {
      setPhase({ kind: "done", text: instanceTestResultSentence(answer, zone) });
    }
  }
  if (phase.kind === "waiting" && status.isError && !answer) {
    reportFailureShown(status.error);
    setPhase({
      kind: "done",
      text: `The test could not be sent. ${failureMessage(status.error, "Try again.")}`,
    });
  }

  const busy = send.isPending || (phase.kind === "waiting" && !phase.tooLong);

  function sendTest() {
    if (busy) return;
    setPhase({ kind: "idle" });
    send.mutate(
      { subject, outcome },
      {
        onSuccess: (accepted) =>
          setPhase({ kind: "waiting", eventId: accepted.eventId, tooLong: false }),
        onError: (error) => {
          reportFailureShown(error);
          setPhase({ kind: "done", text: refusalSentence(error, zone) });
        },
      },
    );
  }

  const current =
    phase.kind === "waiting" && answer?.eventId === phase.eventId ? answer : undefined;
  const line = testLine(phase, send.isPending, current);

  return (
    <section
      aria-labelledby="test-notification-heading"
      className="flex flex-col gap-2 rounded-lg border border-border bg-card p-4"
    >
      <SkyText as="h3" size={300} weight="semibold" block id="test-notification-heading">
        Send a test notification
      </SkyText>
      <SkyText as="p" size={200} color="muted" block>
        The test goes to the saved rule&apos;s own addresses and, when its Teams switch is on, the
        instance&apos;s Teams channel. It never goes to the L1 team.
      </SkyText>
      <div className="flex flex-wrap items-end gap-3">
        <SkyDropdown
          label="What"
          options={subjects.map(subjectOption)}
          selectedOptions={[subject]}
          onOptionSelect={(values) => {
            const next = subjects.find((value) => value === values[0]);
            if (next) setSubject(next);
          }}
          testId="test-notification-subject"
        />
        <SkyDropdown
          label="When"
          options={NOTIFY_OUTCOMES.map(outcomeOption)}
          selectedOptions={[outcome]}
          onOptionSelect={(values) => {
            const next = NOTIFY_OUTCOMES.find((value) => value === values[0]);
            if (next) setOutcome(next);
          }}
          testId="test-notification-outcome"
        />
        {/* Never `disabled` or `loading` while the test is out: both disable the button, and a
            disabled button drops the focus the reader had on it (R14). A second press while
            the line says "Sending…" is ignored instead. */}
        <SkyButton
          appearance="secondary"
          aria-disabled={busy}
          testId="test-notification-send"
          onClick={sendTest}
        >
          Send a test notification
        </SkyButton>
      </div>
      <div role="status" data-testid="test-notification-status">
        {line ? <SkyText size={200}>{line}</SkyText> : null}
      </div>
    </section>
  );
}
